import React, { useEffect, useState } from 'react';
import { Plus, Edit } from 'lucide-react';
import cstyles from './CategoriesView.module.css';
import styles from './CategoriesSettings.module.css';
import { toast } from 'react-toastify';
import {
  getAllPageMeta,
  createPageMeta,
  updatePageMeta
} from '../../../../api/PageMetaManager';

const PageMetaModal = ({ isOpen, onClose, initialData, onSubmit }) => {
  const [pagename, setpagename] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaKeywords, setMetaKeywords] = useState('');
  const [metaDescription, setMetaDescription] = useState('');

  useEffect(() => {
    if (initialData) {
      setpagename(initialData.pagename || '');
      setMetaTitle(initialData.meta_title || '');
      setMetaKeywords(initialData.meta_keywords || '');
      setMetaDescription(initialData.meta_description || '');
    } else {
      setpagename('');
      setMetaTitle('');
      setMetaKeywords('');
      setMetaDescription('');
    }
  }, [initialData, isOpen]);

  const resetFields = () => {
    setpagename('');
    setMetaTitle('');
    setMetaKeywords('');
    setMetaDescription('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!pagename.trim()) {
      toast.error('Page name is required.');
      return;
    }

    onSubmit({
      pagename: pagename.trim(),
      meta_title: metaTitle.trim(),
      meta_keywords: metaKeywords.trim(),
      meta_description: metaDescription.trim()
    });

    resetFields();      // clear fields after submit
    onClose();          // close modal
  };

  const handleClose = () => {
    resetFields();      // clear fields on cancel
    onClose();          // close modal
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <h2>{initialData ? 'Edit Page Metadata' : 'Add Page Metadata'}</h2>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup} style={{ width: '90%' }}>
            <label>Page Name*</label>
            <input
              type="text"
              value={pagename}
              onChange={(e) => setpagename(e.target.value)}
              placeholder="Enter page name"
              className={styles.modalInput}
              required
            />
          </div>

          <div className={styles.formGroup} style={{ width: '90%' }}>
            <label>Meta Title</label>
            <textarea
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              rows="2"
              placeholder="Enter meta title"
              className={styles.modalInput}
            />
          </div>

          <div className={styles.formGroup} style={{ width: '90%' }}>
            <label>Meta Keywords</label>
            <textarea
              value={metaKeywords}
              onChange={(e) => setMetaKeywords(e.target.value)}
              rows="2"
              placeholder="Enter meta keywords"
              className={styles.modalInput}
            />
          </div>

          <div className={styles.formGroup} style={{ width: '90%' }}>
            <label>Meta Description</label>
            <textarea
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              rows="3"
              placeholder="Enter meta description"
              className={styles.modalInput}
            />
          </div>

          <div className={styles.modalButtons}>
            <button type="submit" className={styles.addButton}>
              {initialData ? 'Update' : 'Add'}
            </button>
            <button type="button" onClick={handleClose} className={styles.cancelButton}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PagesSettings = () => {
  const [metaList, setMetaList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMeta, setEditMeta] = useState(null);

  useEffect(() => {
    fetchMetaList();
  }, []);

  const fetchMetaList = async () => {
    try {
      const data = await getAllPageMeta();
      setMetaList(data);
    } catch (err) {
      toast.error('Failed to fetch metadata');
    }
  };

  const handleAdd = async (meta) => {
    try {
      await createPageMeta({ ...meta });
      toast.success('Page meta added');
      fetchMetaList();
    } catch (err) {
      toast.error('Failed to add page meta');
    }
  };

  const handleEdit = async (meta) => {
    try {
      await updatePageMeta(editMeta.id, meta);
      toast.success('Page meta updated');
      fetchMetaList();
    } catch (err) {
      toast.error('Failed to update page meta');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <h2 className={styles.headerTitle}>Page Metadata Settings</h2>
        <button
          className={styles.addButton}
          onClick={() => {
            setEditMeta(null);
            setIsModalOpen(true);
          }}
        >
          <Plus size={18} /> Add Page Metadata
        </button>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Page Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {metaList.map((meta) => (
              <tr key={meta.id}>
                <td>{meta.pagename}</td>
                <td>
                  <button
                    className={styles.iconButton}
                    onClick={() => {
                      setEditMeta(meta);
                      setIsModalOpen(true);
                    }}
                  >
                    <Edit size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PageMetaModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialData={editMeta}
        onSubmit={editMeta ? handleEdit : handleAdd}
      />
    </div>
  );
};

export default PagesSettings;
